import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { useWithdrawRequest } from '../store/slice/withdrawRequestSlice';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionButtonGroup, ActionLink } from '../components/ActionControls';

const PAGE_SIZE = 20;

function formatCurrency(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function maskAccountNumber(accountNumber) {
  if (!accountNumber) return '—';
  const str = String(accountNumber);
  if (str.length <= 4) return str;
  return `****${str.slice(-4)}`;
}

function statusColor(status) {
  switch ((status || '').toLowerCase()) {
    case 'pending':
      return 'status-pill-warning';
    case 'approved':
    case 'completed':
      return 'status-pill-success';
    case 'rejected':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return 'status-pill-muted';
  }
}

function WithdrawRequestPage() {
  const {
    withdrawRequests,
    pagination,
    loading,
    error,
    fetchWithdrawRequests,
    updateWithdrawRequestStatus,
  } = useWithdrawRequest();

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
  });
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const apiFilters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      status: filters.status,
    }),
    [page, filters.status]
  );

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    fetchWithdrawRequests(apiFilters);
  }, [apiFilters, fetchWithdrawRequests]);

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setPage(1);
    }
    setFilters((p) => ({ ...p, [key]: value }));
  };

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'status',
      type: 'select',
      label: 'Status',
      value: filters.status,
      defaultValue: 'all',
      options: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'completed', label: 'Completed' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.status && filters.status !== 'all') count++;
    return count;
  }, [filters]);

  const filteredRequests = useMemo(() => {
    if (!filters.search.trim()) return withdrawRequests;

    const term = filters.search.toLowerCase();
    return withdrawRequests.filter(
      (r) =>
        (r.vendor_name || '').toLowerCase().includes(term) ||
        (r.salon_name || '').toLowerCase().includes(term) ||
        (r.bank_name || '').toLowerCase().includes(term) ||
        (r.account_number || '').includes(term) ||
        (r.vendor_email || '').toLowerCase().includes(term)
    );
  }, [withdrawRequests, filters.search]);

  const pendingOnPage = useMemo(
    () => filteredRequests.filter((r) => r.status === 'pending'),
    [filteredRequests]
  );

  const pendingTotal = useMemo(
    () => pendingOnPage.reduce((s, r) => s + (parseFloat(r.requested_amount) || 0), 0),
    [pendingOnPage]
  );

  const currentPage = pagination.page || page;
  const totalPages = pagination.totalPages || 0;
  const totalRequests = pagination.total || 0;
  const canGoPrev = currentPage > 1;
  const canGoNext = totalPages > 0 && currentPage < totalPages;

  const handleApprove = (id) => {
    updateWithdrawRequestStatus({ id, status: 'approved' }).then((result) => {
      if (!result.error) {
        toast.success('Withdrawal request approved');
        fetchWithdrawRequests(apiFilters);
      }
    });
  };

  const handleComplete = (id) => {
    updateWithdrawRequestStatus({ id, status: 'completed' }).then((result) => {
      if (!result.error) {
        toast.success('Withdrawal marked as completed');
        fetchWithdrawRequests(apiFilters);
      }
    });
  };

  const handleReject = () => {
    if (!rejectModal) return;

    updateWithdrawRequestStatus({
      id: rejectModal.id,
      status: 'rejected',
      admin_note: rejectReason.trim() || undefined,
    }).then((result) => {
      if (!result.error) {
        toast.success('Withdrawal request rejected');
        fetchWithdrawRequests(apiFilters);
      }
      setRejectModal(null);
      setRejectReason('');
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Withdraw Requests</h1>
          <p className="mt-1 text-slate-500 text-sm">Review and manage vendor withdrawal requests</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Requests</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{totalRequests}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">On This Page</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredRequests.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Pending (page)</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{pendingOnPage.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Pending Amount (page)</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{formatCurrency(pendingTotal)}</p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search vendor, salon, bank, or account..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Salon</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Bank Details</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Requested</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Deduction</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Net</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((req) => (
                <tr key={req.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                  <td className="py-4 px-6 text-slate-600">
                    {req.created_at ? moment(req.created_at).format('DD/MM/YYYY') : '—'}
                  </td>
                  <td className="py-4 px-6">
                    <p className="font-medium text-slate-800">{req.vendor_name || '—'}</p>
                    {req.vendor_email && <p className="text-sm text-slate-500">{req.vendor_email}</p>}
                  </td>
                  <td className="py-4 px-6 text-slate-600">{req.salon_name || '—'}</td>
                  <td className="py-4 px-6 text-slate-600">
                    <p>{req.bank_name || '—'}</p>
                    <p className="text-sm text-slate-400">
                      {req.account_holder_name ? `${req.account_holder_name} · ` : ''}
                      {maskAccountNumber(req.account_number)}
                      {req.ifsc_code ? ` · ${req.ifsc_code}` : ''}
                    </p>
                  </td>
                  <td className="py-4 px-6 text-right font-semibold text-slate-800">
                    {formatCurrency(req.requested_amount)}
                  </td>
                  <td className="py-4 px-6 text-right text-slate-600">
                    {req.deduction_percentage != null && (
                      <span className="text-xs text-slate-400 block">{req.deduction_percentage}%</span>
                    )}
                    {formatCurrency(req.deduction_amount)}
                  </td>
                  <td className="py-4 px-6 text-right font-semibold text-emerald-700">
                    {formatCurrency(req.net_amount)}
                  </td>
                  <td className="py-4 px-6">
                    <span className={`status-pill capitalize ${statusColor(req.status)}`}>
                      {req.status || 'pending'}
                    </span>
                    {req.admin_note && req.status === 'rejected' && (
                      <p className="mt-1 text-xs text-slate-400 max-w-[160px] truncate" title={req.admin_note}>
                        {req.admin_note}
                      </p>
                    )}
                  </td>
                  <td className="py-4 px-6 text-right">
                    <ActionButtonGroup>
                      <ActionLink
                        to={`/withdraw-requests/${req.id}`}
                        variant="neutral"
                        className="!px-2"
                        title="View details"
                        aria-label="View details"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </ActionLink>
                      {req.status === 'pending' && (
                        <>
                          <ActionButton
                            type="button"
                            variant="success"
                            onClick={() => handleApprove(req.id)}
                            disabled={loading}
                          >
                            Approve
                          </ActionButton>
                          <ActionButton
                            type="button"
                            variant="danger"
                            onClick={() => setRejectModal(req)}
                          >
                            Reject
                          </ActionButton>
                        </>
                      )}
                      {req.status === 'approved' && (
                        <ActionButton
                          type="button"
                          variant="success"
                          onClick={() => handleComplete(req.id)}
                          disabled={loading}
                        >
                          Mark Completed
                        </ActionButton>
                      )}
                    </ActionButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && withdrawRequests.length === 0 && (
          <div className="py-16 text-center text-slate-500">Loading withdraw requests...</div>
        )}
        {!loading && filteredRequests.length === 0 && (
          <div className="py-16 text-center text-slate-500">No withdraw requests found.</div>
        )}

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
            <p className="text-sm text-slate-500">
              Page {currentPage} of {totalPages} · {totalRequests} requests total
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={!canGoPrev || loading}
                className="px-4 py-2 rounded-premium border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!canGoNext || loading}
                className="px-4 py-2 rounded-premium border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setRejectModal(null)}>
          <div className="card-premium p-6 max-w-md w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 mb-2">Reject Withdraw Request</h3>
            <p className="text-sm text-slate-600 mb-4">
              Rejecting request from <strong>{rejectModal.vendor_name}</strong> for{' '}
              {formatCurrency(rejectModal.requested_amount)}.
            </p>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Admin note (optional)</label>
            <textarea
              placeholder="Provide a reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows="3"
              className="input-premium resize-none mb-4"
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason('');
                }}
                className="btn-premium bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Cancel
              </button>
              <button onClick={handleReject} className="btn-primary" disabled={loading}>
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WithdrawRequestPage;
